import React, { createContext, useContext, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

interface ToastItem {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastItem['type']) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

const slideIn = keyframes`
  from { transform: translateX(110%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Container = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ToastBox = styled.div<{ type: ToastItem['type'] }>`
  padding: 12px 20px;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  animation: ${slideIn} 0.3s ease;
  max-width: 350px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  background: ${p =>
    p.type === 'success' ? '#10b981' :
    p.type === 'error' ? '#ef4444' : '#7c3aed'
  };
`;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  let counter = 0;

  const showToast = useCallback((message: string, type: ToastItem['type'] = 'info') => {
    const id = ++counter;
    setToasts(p => [...p, { id, message, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Container>
        {toasts.map(t => (
          <ToastBox key={t.id} type={t.type}>{t.message}</ToastBox>
        ))}
      </Container>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
